import { type PointerEvent, type Ref, type SVGProps, useCallback, useId, useRef } from "react";
import { hurryIcon, playIcon } from "./animate.js";
import type { IconData, IconStyle } from "./types.js";

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "ref" | "children"> {
  /** An icon module's default export, e.g. `import packs from "@loot-os/icons/icons/packs"`. */
  icon: IconData;
  /** Drawing style. Defaults to `rounded`. */
  variant?: IconStyle;
  /** Width and height (px or any CSS length). Defaults to 24. */
  size?: number | string;
  /** Play the animation on hover (mouse/pen) and tap (touch). Defaults to true. */
  animated?: boolean;
  /** Accessible name. Omit for a decorative icon beside visible text. */
  label?: string;
  ref?: Ref<SVGSVGElement>;
}

/** An icon drawn in `currentColor` that animates on hover or tap. */
export function Icon({
  icon,
  variant = "rounded",
  size = 24,
  animated = true,
  label,
  ref,
  onPointerEnter,
  onPointerLeave,
  onPointerDown,
  ...rest
}: IconProps) {
  const own = useRef<SVGSVGElement | null>(null);
  // swapping the drawing under a resting pointer makes the browser report a fresh enter; ignore it
  const inside = useRef(false);
  const idPrefix = `ic${useId().replace(/[^\w-]/g, "")}-`;
  const setRef = useCallback(
    (el: SVGSVGElement | null) => {
      own.current = el;
      if (typeof ref === "function") ref(el);
      else if (ref) ref.current = el;
    },
    [ref],
  );
  const play = (e: PointerEvent<SVGSVGElement>, touch: boolean) => {
    if (animated && own.current && (e.pointerType === "touch") === touch)
      playIcon(own.current, icon, variant);
  };
  return (
    <svg
      // a new style is a new element, so a running animation never restores the old drawing
      key={`${icon.id}:${variant}`}
      ref={setRef}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
      overflow="hidden"
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
      {...rest}
      onPointerEnter={(e) => {
        if (inside.current) return;
        inside.current = true;
        onPointerEnter?.(e);
        play(e, false);
      }}
      onPointerDown={(e) => {
        onPointerDown?.(e);
        play(e, true);
      }}
      onPointerLeave={(e) => {
        inside.current = false;
        onPointerLeave?.(e);
        // a lifted finger also "leaves"; only a mouse or pen moving away should speed the animation up
        if (e.pointerType !== "touch" && own.current) hurryIcon(own.current);
      }}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: markup is the package's own generated icon data
      dangerouslySetInnerHTML={{ __html: icon.styles[variant].svg.replaceAll("{id}", idPrefix) }}
    />
  );
}
